import { Alert, Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { Claims, CreateTissueOfferRequest, FormSlugs, ContactData, Surgery, Tissue, TissueOffer } from "../../models";
import { usePersonQuery, useTissueOfferQuery, useTissueOfferStatusQuery } from "../../queries";
import { TissueOfferMethod, tissueOfferMethods } from ".";
import { useState, useEffect, useRef, useMemo } from "react";
import { displayDate, getFaxageEmail } from "../../helper-functions";
import { usePermissions, useProfile } from "../../contexts";
import { TissueInformationForm, useDownloadForm } from "../forms";
import { PersonModal } from "../people";
import { BooleanIcon, LoadingSpinner } from "../../component-library";

interface CreateOfferModalProps {
    show: boolean;
    onHide: () => void;
    surgery: Surgery | undefined;
    tissue: Tissue | undefined;
}

export function CreateOfferModal({ show, onHide, surgery, tissue }: Readonly<CreateOfferModalProps>) {
    const { currentUser } = useProfile();
    const { hasPermission } = usePermissions();
    const canCreateOffer = hasPermission(Claims.OfferTissue);
    const { createMutation } = useTissueOfferQuery();
    const {
        listQuery: { data: statuses, isFetching: isStatusesFetching },
    } = useTissueOfferStatusQuery();
    const {
        surgeonEmailsQuery: { data: surgeonEmails, isFetching: isSurgeonEmailsFetching },
    } = usePersonQuery({
        queryType: "surgeon-emails",
        surgeonIds: surgery?.surgeon?.id ? [surgery.surgeon.id] : undefined,
    });
    const [showSurgeonModal, setShowSurgeonModal] = useState<boolean>(false);
    const [selectedContacts, setSelectedContacts] = useState<ContactData[]>([]);
    const [autoAccept, setAutoAccept] = useState<boolean>(surgery?.surgeon?.isCriteriaBased ?? false);
    const [method, setMethod] = useState<TissueOfferMethod | "">(TissueOfferMethod.Email);
    const [emailBody, setEmailBody] = useState<string>("");
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const formRef = useRef<HTMLDivElement>(null);
    const offeredStatus = statuses?.find((s) => s.name === "Offered");
    const isLoading = isStatusesFetching || isSurgeonEmailsFetching;

    const emailSummaries = useMemo(
        () => surgeonEmails?.[0]?.emailSummaries?.filter((s) => s.method === TissueOfferMethod.Email) ?? [],
        [surgeonEmails]
    );
    const faxSummaries = useMemo(
        () => surgeonEmails?.[0]?.emailSummaries?.filter((s) => s.method === TissueOfferMethod.Fax) ?? [],
        [surgeonEmails]
    );

    const { generatePdfFile } = useDownloadForm({
        baseFileName: `TIF`,
        formRef,
        formData: undefined,
        formSlugProp: FormSlugs.TIF,
    });

    const autoAcceptText =
        "This tissue has been accepted for your surgical needs. Please call (855) 309-8243 for more information. Thank you.";
    const nonAutoAcceptText =
        "Please review this tissue for your surgical needs. Reply to this email or call (855) 309-8243 to accept or decline. Please respond as soon as possible. Thank you.";

    useEffect(() => {
        if (surgery && tissue && !isLoading) {
            setDataLoaded(false);
        }
    }, [isLoading, surgery, tissue]);

    useEffect(() => {
        if (!dataLoaded && !isLoading && surgery && tissue) {
            setMethod(TissueOfferMethod.Email);
            setSelectedContacts([]);
            if (surgery.surgeon?.isCriteriaBased) {
                setAutoAccept(true);
                setEmailBody(autoAcceptText);
            } else {
                setAutoAccept(false);
                setEmailBody(nonAutoAcceptText);
            }
            setDataLoaded(true);
        }
    }, [dataLoaded, isLoading, surgery, tissue]);

    useEffect(() => {
        if (autoAccept && (!emailBody || emailBody === nonAutoAcceptText)) {
            setEmailBody(autoAcceptText);
        }
        if (!autoAccept && (!emailBody || emailBody === autoAcceptText)) {
            setEmailBody(nonAutoAcceptText);
        }
    }, [autoAccept, emailBody]);

    const toggleContact = (email: ContactData) => {
        if (selectedContacts.some((e) => e.value === email.value)) {
            setSelectedContacts(selectedContacts.filter((e) => e.value !== email.value));
        } else {
            setSelectedContacts((prev) => [...prev, email]);
        }
    };

    const recipientEmails = useMemo(() => {
        if (method === TissueOfferMethod.Email) {
            return selectedContacts.filter((e) => e.method === TissueOfferMethod.Email).map((e) => e.value);
        }
        if (method === TissueOfferMethod.Fax) {
            return selectedContacts
                .filter((e) => e.method === TissueOfferMethod.Fax)
                .map((e) => getFaxageEmail(e.value));
        }
        return [];
    }, [method, selectedContacts]);

    const createOffer = async () => {
        if (!tissue || !surgery || !canCreateOffer) return;

        if (!formRef.current) {
            console.error("Failed to generate PDF: Form content is missing.");
            return;
        }

        const htmlContent = formRef.current.innerHTML;
        const base64file = await generatePdfFile(htmlContent);

        if (!base64file) {
            console.error("Failed to generate PDF file.");
            return;
        }

        const tissueOffer: Partial<TissueOffer> = {
            tissueId: tissue.id,
            surgeryId: surgery.id,
            surgeonId: surgery.surgeon?.id,
            method,
            tissueOfferStatusId: offeredStatus?.id,
        };

        const request: CreateTissueOfferRequest = {
            offer: tissueOffer,
            tifFile: base64file,
            isAutoAccept: autoAccept,
            recipientEmails,
            emailBody,
        };

        await createMutation.mutateAsync(request);
        onHide();
        setMethod("");
    };

    if (isLoading)
        return (
            <Modal show={show} onHide={onHide} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Offer Tissue</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <LoadingSpinner />
                </Modal.Body>
            </Modal>
        );

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Offer Tissue</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ul>
                        <li>
                            <strong>Surgeon:</strong> {surgery?.surgeon?.firstName} {surgery?.surgeon?.lastName}
                            <Button variant="link" onClick={() => setShowSurgeonModal(true)} className="ms-2">
                                View surgeon preferences
                            </Button>
                        </li>
                        <li>
                            <strong>Surgery Date:</strong> {displayDate(surgery?.scheduledOnUtc)}
                        </li>
                        <li>
                            <strong>Surgery Type:</strong> {surgery?.surgeryType?.name}
                        </li>
                        <li>
                            <strong>Tissue:</strong> {tissue?.number}
                        </li>
                    </ul>
                    {surgery?.surgeon?.isCriteriaBased && <Alert variant="info">This surgeon is criteria-based.</Alert>}
                    <Row>
                        <Col className="mb-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Contact Method</Form.Label>
                                <Form.Select
                                    required
                                    value={method}
                                    onChange={(e) => setMethod(e.target.value as TissueOfferMethod)}
                                >
                                    <option value="" disabled>
                                        Select a contact method...
                                    </option>
                                    {tissueOfferMethods.map((m) => (
                                        <option key={m} value={m}>
                                            {m}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col className="mb-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Auto Accept</Form.Label>
                                <Form.Check
                                    type="checkbox"
                                    checked={autoAccept}
                                    onChange={(e) => setAutoAccept(e.target.checked)}
                                    id="auto-accept"
                                    label="Automatically accept the offer for this surgeon"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {method === TissueOfferMethod.Email && (
                        <>
                            <Row>
                                <Col className="mb-3">
                                    {emailSummaries.length ? (
                                        <Form.Group>
                                            <Form.Label>Select Email Recipients</Form.Label>
                                            <Table hover>
                                                <thead>
                                                    <tr>
                                                        <th />
                                                        <th>Name</th>
                                                        <th>Email</th>
                                                        <th>Type</th>
                                                        <th>Contact Preference</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {emailSummaries.map((data) => (
                                                        <tr
                                                            key={data.name}
                                                            onClick={() => toggleContact(data)}
                                                            className="clickable"
                                                        >
                                                            <td className="text-center">
                                                                {selectedContacts.some(
                                                                    (e) => e.value === data.value
                                                                ) && <BooleanIcon value={true} />}
                                                            </td>
                                                            <td>{data.name}</td>
                                                            <td>{data.value}</td>
                                                            <td>{data.contactType}</td>
                                                            <td>{data.preferredContactMethod}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Form.Group>
                                    ) : (
                                        <Alert variant="warning">No emails found for surgeon</Alert>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Email Body</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            value={emailBody}
                                            onChange={(e) => setEmailBody(e.target.value)}
                                            style={{ height: "150px" }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}

                    {method === TissueOfferMethod.Fax && (
                        <>
                            <Row>
                                <Col className="mb-3">
                                    {faxSummaries.length ? (
                                        <Form.Group>
                                            <Form.Label>Select Fax Recipients</Form.Label>
                                            <Table hover>
                                                <thead>
                                                    <tr>
                                                        <th />
                                                        <th>Name</th>
                                                        <th>Fax</th>
                                                        <th>Type</th>
                                                        <th>Contact Preference</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {faxSummaries.map((data) => (
                                                        <tr
                                                            key={data.name}
                                                            onClick={() => toggleContact(data)}
                                                            className="clickable"
                                                        >
                                                            <td className="text-center">
                                                                {selectedContacts.some(
                                                                    (e) => e.value === data.value
                                                                ) && <BooleanIcon value={true} />}
                                                            </td>
                                                            <td>{data.name}</td>
                                                            <td>{data.value}</td>
                                                            <td>{data.contactType}</td>
                                                            <td>{data.preferredContactMethod}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Form.Group>
                                    ) : (
                                        <Alert variant="warning">No fax numbers found for surgeon</Alert>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Fax Content</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            value={emailBody}
                                            onChange={(e) => setEmailBody(e.target.value)}
                                            style={{ height: "150px" }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button onClick={onHide} variant="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={createOffer}
                        disabled={
                            createMutation.isLoading ||
                            !method ||
                            !canCreateOffer ||
                            (method === TissueOfferMethod.Email && !selectedContacts.length) ||
                            (method === TissueOfferMethod.Fax && !selectedContacts.length)
                        }
                    >
                        {createMutation.isLoading ? "Creating offer..." : "Offer Tissue"}
                    </Button>
                </Modal.Footer>
                <div ref={formRef} hidden={true}>
                    <TissueInformationForm tissueId={tissue?.id} />
                </div>
            </Modal>

            <PersonModal person={surgery?.surgeon} show={showSurgeonModal} onHide={() => setShowSurgeonModal(false)} />
        </>
    );
}
